// src/components/NavBar.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Slide,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";

/* ───────────────── util: esconder AppBar al hacer scroll ─────────────── */
function HideOnScroll({ children }) {
  const lastY = useRef(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const y = window.pageYOffset;
      setShow(y < lastY.current || y < 100);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Slide appear={false} direction="down" in={show}>
      {children}
    </Slide>
  );
}
/* ──────────────────────────────────────────────────────────────────────── */

export default function NavBar() {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [drawer, setDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  /* solid sólo en /cotizar */
  const forceSolid = pathname.startsWith("/cotizar");

  useEffect(() => {
    const onScroll = () => setScrolled(window.pageYOffset > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* -------------- handler para ir a la sección “Servicios” -------------- */
  const goServicios = () => {
    const scroll = () =>
      document
        .getElementById("servicios")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

    if (pathname === "/home") {
      scroll(); // ya estamos en Home → sólo scrolleo
    } else {
      navigate("/home#servicios"); // cambio de ruta (Home.jsx se encargará del scroll)
    }
  };

  /* enlaces del menú */
  const NAV = [
    { to: "/home", label: "Inicio", icon: <HomeIcon /> },
    // marcador especial: no usamos NavLink
    { to: "SERVICIOS", label: "Servicios", icon: <SolarPowerIcon /> },
    { to: "/nosotros", label: "Nosotros", icon: <PeopleAltIcon /> },
    { to: "/contacto", label: "Contacto", icon: <MailOutlineIcon /> },
  ];

  /* estilo de links */
  const linkStyle = {
    mx: 2,
    px: 1,
    fontWeight: 600,
    textTransform: "none",
    fontSize: 16,
    fontFamily: '"Ethos Nova", sans-serif',
    color: "#fff",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      bottom: -4,
      width: 0,
      height: 3,
      borderRadius: 2,
      bgcolor: theme.palette.primary.main,
      transition: "width .3s ease",
    },
    "&:hover": { color: theme.palette.primary.main },
    "&.active": {
      color: theme.palette.primary.main,
      "&::after": { width: "100%" },
    },
  };

  /* ─────────────────────────────────────────────────────────── */
  return (
    <HideOnScroll>
      <AppBar
        position="fixed"
        elevation={forceSolid || scrolled ? 4 : 0}
        sx={{
          bgcolor:
            forceSolid || scrolled ? theme.palette.grey[900] : "transparent",
          transition: "background .3s, box-shadow .3s",
          backdropFilter: forceSolid || scrolled ? "none" : "blur(4px)",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 2, md: 6 },
            height: 88,
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* logo */}
          <Box
            component={NavLink}
            to="/home"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              mr: { xs: 2, md: 4 },
            }}
          >
            <Logo height={{ xs: "40px", md: "48px" }} />
          </Box>

          {/* enlaces desktop */}
          <Box
            sx={{
              ml: "auto",
              display: { xs: "none", md: "flex" },
              alignItems: "center",
            }}
          >
            {NAV.map(({ to, label }) =>
              to === "SERVICIOS" ? (
                <Button key={label} onClick={goServicios} sx={linkStyle}>
                  {label}
                </Button>
              ) : (
                <Button
                  key={label}
                  component={NavLink}
                  to={to}
                  end
                  sx={linkStyle}
                >
                  {label}
                </Button>
              )
            )}

            {/* CTA */}
            <Button
              component={NavLink}
              to="/cotizar"
              variant="contained"
              sx={{
                ml: 4,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontFamily: '"Ethos Nova", sans-serif',
                textTransform: "none",
                bgcolor: theme.palette.primary.main,
                color: "#fff",
                boxShadow: "0 4px 12px rgba(224,106,0,.4)",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.9),
                  transform: "translateY(-2px)",
                },
              }}
            >
              Cotizar ahora
            </Button>
          </Box>

          {/* icono menú móvil */}
          <Box sx={{ ml: "auto", display: { xs: "flex", md: "none" } }}>
            <IconButton onClick={() => setDrawer(true)} sx={{ color: "#fff" }}>
              <MenuIcon fontSize="large" />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Drawer móvil */}
        <Drawer
          anchor="right"
          open={drawer}
          onClose={() => setDrawer(false)}
          PaperProps={{
            sx: { width: 280, bgcolor: theme.palette.grey[900], color: "#fff" },
          }}
        >
          <Box
            sx={{
              px: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: 88,
            }}
          >
            <Logo height="40px" />
            <IconButton onClick={() => setDrawer(false)} sx={{ color: "#fff" }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: alpha("#fff", 0.25) }} />

          <List>
            {NAV.map(({ to, label, icon }) =>
              to === "SERVICIOS" ? (
                <ListItemButton
                  key={label}
                  onClick={() => {
                    goServicios();
                    setDrawer(false);
                  }}
                  sx={{ color: "#fff" }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontFamily: '"Ethos Nova", sans-serif',
                    }}
                  />
                </ListItemButton>
              ) : (
                <ListItemButton
                  key={label}
                  component={NavLink}
                  to={to}
                  selected={pathname === to}
                  onClick={() => setDrawer(false)}
                  sx={{
                    color: "#fff",
                    "&.Mui-selected": { bgcolor: alpha("#fff", 0.15) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "#fff" }}>
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontFamily: '"Ethos Nova", sans-serif',
                    }}
                  />
                </ListItemButton>
              )
            )}
          </List>

          <Divider sx={{ borderColor: alpha("#fff", 0.25) }} />

          <Box sx={{ p: 2 }}>
            <Button
              component={NavLink}
              to="/cotizar"
              variant="contained"
              fullWidth
              onClick={() => setDrawer(false)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontFamily: '"Ethos Nova", sans-serif',
                py: 1.5,
                bgcolor: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.9),
                },
              }}
            >
              Cotizar ahora
            </Button>
          </Box>
        </Drawer>
      </AppBar>
    </HideOnScroll>
  );
}
